import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DateInput from '../components/DateInput';
import { PersonOfInterest, DriverLicense } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

interface PersonErrors {
  name: string;
  surname: string;
  email: string;
  designation: string;
  status: string;
  contact_number: string;
  id_passport_number: string;
  date_of_birth: string;
  drivers_license_codes: string;
  server: string;
}

interface DriverLicenseEntry {
  code: string;
  dateOfFirstIssue: string;
  expiresOn: string;
}

const AddPersonPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<number | null>(null);
  const [usedLicenseCodes, setUsedLicenseCodes] = useState<string[]>([]);
  const [availableLicenseCodes] = useState([
    'Learners', 'A', 'A1', 'B', 'C', 'C1', 'D', 'D1', 'EB', 'EC', 'EC1', 'ED', 'ED1'
  ]);

  const [formData, setFormData] = useState<Partial<PersonOfInterest>>({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: 'active',
    contact_number: '+27',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: [],
  });

  const [errors, setErrors] = useState<PersonErrors>({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  });

  const [returnToUrl, setReturnToUrl] = useState<string | null>(null);

  // Fetch existing person if editing
  useEffect(() => {
    const fetchPersonForEdit = async () => {
      const editId = new URLSearchParams(window.location.search).get('editId');
      if (editId && clientId) {
        setEditingPersonId(parseInt(editId));
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${API_URL}/api/persons-of-interest/${editId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const person = response.data;
          const parsedLicenses = parseDriverLicenses(person.drivers_license_code);
          
                    // Format date of birth for the input field (YYYY-MM-DD)
          let formattedDate = '';
          if (person.date_of_birth) {
            const date = new Date(person.date_of_birth);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
            }
          }
          
          setFormData({
            name: person.name,
            surname: person.surname,
            email: person.email,
            designation: person.designation,
            status: person.status,
            contact_number: person.contact_number || '+27',
            id_passport_number: person.id_passport_number || '',
            date_of_birth: formattedDate,
            drivers_license_codes: parsedLicenses,
          });
        } catch (error) {
          console.error('Error fetching person for edit:', error);
        }
      }
    };
    
    fetchPersonForEdit();
  }, [clientId]);

  // ADD THIS - Get returnTo parameter from URL
  useEffect(() => {
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    if (returnTo) {
      setReturnToUrl(decodeURIComponent(returnTo));
      console.log('📍 Will return to:', decodeURIComponent(returnTo));
    }
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

    const parseDriverLicenses = (licenseData: any): DriverLicense[] => {
    if (!licenseData) return [];
    
    try {
      // If it's already an array, return it
      if (Array.isArray(licenseData)) {
        return licenseData;
      }
      
      // If it's a string containing pipe-delimited format
      if (typeof licenseData === 'string' && licenseData.includes('|')) {
        const licensePairs = licenseData.split(',');
        return licensePairs.map(pair => {
          const [code, dateOfFirstIssue, expiresOn] = pair.split('|');
          return { 
            code: code?.trim() || '', 
            dateOfFirstIssue: dateOfFirstIssue?.trim() || '', 
            expiresOn: expiresOn?.trim() || '' 
          };
        }).filter(license => license.code && license.code !== '');
      }
      
      // If it's a string that might be JSON
      if (typeof licenseData === 'string' && (licenseData.startsWith('[') || licenseData.startsWith('{'))) {
        try {
          const parsed = JSON.parse(licenseData);
          if (Array.isArray(parsed)) {
            return parsed;
          } else {
            return [parsed];
          }
        } catch (e) {
          // Not valid JSON, treat as single code
          if (licenseData.trim()) {
            return [{ code: licenseData.trim(), dateOfFirstIssue: '', expiresOn: '' }];
          }
        }
      }
      
      // If it's a plain string (single code)
      if (typeof licenseData === 'string' && licenseData.trim()) {
        return [{ code: licenseData.trim(), dateOfFirstIssue: '', expiresOn: '' }];
      }
      
      return [];
    } catch (error) {
      console.error('Error parsing driver licenses:', error);
      return [];
    }
  };

  const getAvailableLicenseCodes = (currentIndex: number) => {
    const currentSelectedCodes = (formData.drivers_license_codes as DriverLicense[])
      .map(license => license.code)
      .filter(code => code !== '');
    
    return availableLicenseCodes.filter(code => {
      const selectedInOtherDropdowns = currentSelectedCodes.filter(
        (selectedCode, index) => index !== currentIndex && selectedCode === code
      );
      return selectedInOtherDropdowns.length === 0 && !usedLicenseCodes.includes(code);
    });
  };

  const canAddMoreLicenses = () => {
    const currentCodes = (formData.drivers_license_codes as DriverLicense[]).map(l => l.code);
    return availableLicenseCodes.some(code => 
      !currentCodes.includes(code) && !usedLicenseCodes.includes(code)
    );
  };

  const addDriverLicense = () => {
    const currentCodes = (formData.drivers_license_codes as DriverLicense[]).map(l => l.code);
    const availableCode = availableLicenseCodes.find(code => 
      !currentCodes.includes(code) && !usedLicenseCodes.includes(code)
    );
    
    if (availableCode) {
      setFormData(prev => ({
        ...prev,
        drivers_license_codes: [
          ...(prev.drivers_license_codes as DriverLicense[]),
          { code: availableCode, dateOfFirstIssue: '', expiresOn: '' }
        ]
      }));
    }
  };

  const removeDriverLicense = (index: number) => {
    setFormData(prev => ({
      ...prev,
      drivers_license_codes: (prev.drivers_license_codes as DriverLicense[]).filter((_, i) => i !== index)
    }));
  };

  const handleDriverLicenseChange = (index: number, field: keyof DriverLicense, value: string) => {
    setFormData(prev => ({
      ...prev,
      drivers_license_codes: (prev.drivers_license_codes as DriverLicense[]).map((license, i) =>
        i === index ? { ...license, [field]: value } : license
      )
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'contact_number') {
      let numberPart = value.startsWith('+27') ? value.slice(3) : value;
      numberPart = numberPart.replace(/[^0-9]/g, '');
      if (numberPart.length > 9) numberPart = numberPart.slice(0, 9);
      const fullNumber = '+27' + numberPart;
      setFormData(prev => ({ ...prev, [name]: fullNumber }));
      setErrors(prev => ({ ...prev, contact_number: '', server: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '', server: '' }));
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = { ...errors };
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!formData.surname?.trim()) {
      newErrors.surname = 'Surname is required';
      isValid = false;
    }
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }
    if (!formData.designation?.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }
    if (!formData.status?.trim()) {
      newErrors.status = 'Status is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientId || isNaN(parseInt(clientId))) {
      setErrors(prev => ({ ...prev, server: 'Client ID is missing or invalid' }));
      return;
    }
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const driverLicensesArray = formData.drivers_license_codes as DriverLicense[];
      
      const personData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        designation: formData.designation,
        status: formData.status,
        contact_number: formData.contact_number || '',
        id_passport_number: formData.id_passport_number || '',
        date_of_birth: formData.date_of_birth || '',
        drivers_license_codes: driverLicensesArray,
        clientId: parseInt(clientId),
      };
      
            if (editingPersonId) {
        await axios.put(
          `${API_URL}/api/persons-of-interest/${editingPersonId}`,
          personData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/persons-of-interest`,
          personData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Navigate back to returnTo URL if provided, otherwise go to persons tab
      if (returnToUrl) {
        console.log('🔄 Returning to:', returnToUrl);
        navigate(returnToUrl);
      } else {
        // ✅ Pass state to tell the Personal page to open the Person of Interest tab
        navigate(`/personal/${clientId}`, { state: { activeTab: 'personOfInterest' } });
      }
      
    } catch (error: any) {
      setErrors(prev => ({
        ...prev,
        server: error.response?.data?.message || `Failed to ${editingPersonId ? 'update' : 'add'} person`
      }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingPersonId ? 'Edit Person of Interest' : 'Add Person of Interest'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">
              <button
              onClick={() => {
                if (returnToUrl) {
                  navigate(returnToUrl);
                } else {
                  // ✅ Pass state to tell the Personal page to open the Person of Interest tab
                  navigate(`/personal/${clientId}`, { state: { activeTab: 'person-of-interest' } });
                }
              }}
              className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              ← Back to {returnToUrl ? 'Vehicle' : 'Client'}
            </button>
            
            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                {editingPersonId ? 'Edit Person of Interest' : 'Add New Person of Interest'}
              </h3>
              
              {errors.server && <p className="text-red-500 mb-4">{errors.server}</p>}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>
                  
                  {/* Surname */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Surname *</label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.surname ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.surname && <p className="text-red-500 text-sm mt-1">{errors.surname}</p>}
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  
                  {/* Designation */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Designation *</label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.designation ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.designation && <p className="text-red-500 text-sm mt-1">{errors.designation}</p>}
                  </div>
                  
                  {/* Status */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.status ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status}</p>}
                  </div>
                  
                  {/* Contact Number */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Contact Number</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600 text-sm">
                        +27
                      </span>
                      <input
                        type="tel"
                        name="contact_number"
                        value={formData.contact_number?.replace('+27', '') || ''}
                        onChange={handleInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter 9-digit number"
                        maxLength={9}
                      />
                    </div>
                    {errors.contact_number && <p className="text-red-500 text-sm mt-1">{errors.contact_number}</p>}
                  </div>
                  
                  {/* ID/Passport Number */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">ID/Passport Number</label>
                    <input
                      type="text"
                      name="id_passport_number"
                      value={formData.id_passport_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Date of Birth */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Date of Birth</label>
                    <DateInput
                      value={formData.date_of_birth || ''}
                      onChange={(date) => {
                        setFormData(prev => ({ ...prev, date_of_birth: date }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* Driver's License Codes Section */}
                <div>
                  <label className="block text-gray-700 font-medium mb-4">Driver's License Codes</label>
                  <div className="space-y-3">
                    {(formData.drivers_license_codes as DriverLicense[]).length === 0 ? (
                      <p className="text-gray-500 text-sm">No license codes added yet.</p>
                    ) : (
                      (formData.drivers_license_codes as DriverLicense[]).map((license, index) => {
                        const availableCodes = getAvailableLicenseCodes(index);
                        return (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="text-sm font-medium text-gray-700">License Code</label>
                                <select
                                  value={license.code}
                                  onChange={(e) => handleDriverLicenseChange(index, 'code', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="">Select Code</option>
                                  {availableCodes.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">Date of First Issue</label>
                                <DateInput
                                  value={license.dateOfFirstIssue || ''}
                                  onChange={(date) => handleDriverLicenseChange(index, 'dateOfFirstIssue', date)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">Expires On</label>
                                <DateInput
                                  value={license.expiresOn || ''}
                                  onChange={(date) => handleDriverLicenseChange(index, 'expiresOn', date)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end mt-3">
                              <button
                                type="button"
                                onClick={() => removeDriverLicense(index)}
                                className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                              >
                                Remove License
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <button
                      type="button"
                      onClick={addDriverLicense}
                      disabled={!canAddMoreLicenses()}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      + Add Another License
                    </button>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      if (returnToUrl) {
                        navigate(returnToUrl);
                      } else {
                        navigate(`/personal/${clientId}?tab=persons`);
                      }
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md disabled:bg-gray-400"
                  >
                    {isLoading ? 'Saving...' : (editingPersonId ? 'Update Person' : 'Add Person')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPersonPage;